// src/interfaces/user/Navigation.js
import React, { useState } from 'react'; // Import useState
import { useQuotes } from '../../context/QuoteContext';
import { useAuth } from '../../context/AuthContext';

const Navigation = ({ activePage, setActivePage, jobType, setJobType }) => {
  const { currentUser, logout } = useAuth();
  const { stopEditingDraft } = useQuotes();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    // Any additional cleanup as needed
  };
  
  return (
    <nav className="bg-white shadow">
      {/* Main navigation bar content */}
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex justify-between h-10">
          {/* Left side of nav: Logo and Desktop Links */}
          <div className="flex">
            <div className="flex-shrink-0 flex items-center">
             <span className="text-xl font-bold text-gray-800"></span>
            </div>
            <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
              <button
                onClick={() => {
                  stopEditingDraft();
                  setActivePage('calculator');
                  setJobType('large-format');
                }}
                className={`${
                  activePage === 'calculator' && jobType === 'large-format'
                    ? 'border-indigo-500 text-gray-900'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
              >
                Large Format
              </button>
              <button
                onClick={() => {
                  stopEditingDraft();
                  setActivePage('calculator');
                  setJobType('small-format');
                }}
                className={`${
                  activePage === 'calculator' && jobType === 'small-format'
                    ? 'border-indigo-500 text-gray-900'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
              >
                Small Format
              </button>
              {/* NEW: Drafts Tab */}
              <button
                onClick={() => setActivePage('drafts')}
                className={`${
                  activePage === 'drafts'
                    ? 'border-indigo-500 text-gray-900'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
              >
                Drafts
              </button>
              <button
                onClick={() => setActivePage('quotes')}
                className={`${
                  activePage === 'quotes'
                    ? 'border-indigo-500 text-gray-900'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
              >
                Orders
              </button>
              {/* NEW: Address Book Tab */}
              <button
                onClick={() => setActivePage('address-book')}
                className={`${
                  activePage === 'address-book'
                    ? 'border-indigo-500 text-gray-900'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
              >
                Address Book
              </button>
            </div>
          </div>

          {/* Mobile calculator tabs - visible only on mobile */}
          <div className="flex sm:hidden justify-center space-x-6 px-4 py-2 border-b">
            <button
              onClick={() => {
                stopEditingDraft();
                setActivePage('calculator');
                setJobType('large-format');
              }}
              className={`${
                activePage === 'calculator' && jobType === 'large-format'
                  ? 'border-indigo-500 text-gray-900'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              } inline-flex items-center px-3 py-2 border-b-2 text-sm font-medium`}
            >
              Large Format
            </button>
            <button
              onClick={() => {
                stopEditingDraft();
                setActivePage('calculator');
                setJobType('small-format');
              }}
              className={`${
                activePage === 'calculator' && jobType === 'small-format'
                  ? 'border-indigo-500 text-gray-900'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              } inline-flex items-center px-3 py-2 border-b-2 text-sm font-medium`}
            >
              Small Format
            </button>
          </div>

          {/* Right side of nav: User Info/Logout (Desktop) and Mobile Menu Button */}
          <div className="hidden sm:ml-6 sm:flex sm:items-center sm:space-x-4">
            {currentUser ? (
              <div className="flex items-center">
                <span className="px-3 py-2 rounded-md text-sm font-medium text-gray-600">
                  Welcome, {currentUser.username}
                </span>
                <button
                  onClick={handleLogout}
                  className="ml-3 px-3 py-1 rounded-md text-sm font-medium bg-gray-200 text-gray-700 hover:bg-gray-300"
                >
                  Logout
                </button>
              </div>
            ) : (
              <span className="px-3 py-2 rounded-md text-sm font-medium text-gray-600">
                Not logged in
              </span>
            )}
          </div>

          {/* Mobile menu button (Hamburger) */}
          <div className="-mr-2 flex items-center sm:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              type="button"
              className="bg-white inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
              aria-controls="mobile-menu"
              aria-expanded={isMobileMenuOpen}
            >
              <span className="sr-only">Open main menu</span>
              {/* Icon when menu is closed. */}
              {!isMobileMenuOpen ? (
                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              ) : (
                // Icon when menu is open.
                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu, show/hide based on menu state. */}
      {isMobileMenuOpen && (
        <div className="sm:hidden" id="mobile-menu">
          <div className="pt-2 pb-3 space-y-1"> {/* This padding creates the space for the items */}
            {/* NEW: Drafts Mobile Button */}
            <button
              onClick={() => { setActivePage('drafts'); setIsMobileMenuOpen(false); }}
              className={`${
                activePage === 'drafts'
                  ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                  : 'border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800'
              } block pl-3 pr-4 py-2 border-l-4 text-base font-medium w-full text-left`}
            >
              Drafts
            </button>
            <button
              onClick={() => { setActivePage('quotes'); setIsMobileMenuOpen(false); }}
              className={`${
                activePage === 'quotes'
                  ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                  : 'border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800'
              } block pl-3 pr-4 py-2 border-l-4 text-base font-medium w-full text-left`}
            >
              Orders
            </button>
            {/* Address Book Mobile Button */}
            <button
              onClick={() => { setActivePage('address-book'); setIsMobileMenuOpen(false); }}
              className={`${
                activePage === 'address-book'
                  ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                  : 'border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800'
              } block pl-3 pr-4 py-2 border-l-4 text-base font-medium w-full text-left`}
            >
              Address Book
            </button>

            {/* Mobile logout button */}
            {currentUser && (
              <button
                onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }}
                className="block w-full text-left pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800"
              >
                Logout ({currentUser.username})
              </button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;